import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { useLang, tField } from "../lib/LanguageContext";
import { api, imageUrl } from "../lib/api";
import SEO from "../components/SEO";

const CATEGORIES = {
    al: {
        all: "Të gjitha",
        beaches: "Plazhe",
        restaurant: "Restorant",
        attractions: "Atraksione",
        tips: "Këshilla",
        general: "Tjera",
    },
    en: {
        all: "All",
        beaches: "Beaches",
        restaurant: "Restaurant",
        attractions: "Attractions",
        tips: "Tips",
        general: "Other",
    },
    it: {
        all: "Tutti",
        beaches: "Spiagge",
        restaurant: "Ristorante",
        attractions: "Attrazioni",
        tips: "Consigli",
        general: "Altro",
    },
    de: {
        all: "Alle",
        beaches: "Strände",
        restaurant: "Restaurant",
        attractions: "Sehenswürdigkeiten",
        tips: "Tipps",
        general: "Sonstiges",
    },
};

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const locale = lang === "al" ? "sq-AL" : lang === "it" ? "it-IT" : lang === "de" ? "de-DE" : "en-GB";
        return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

export default function Blog() {
    const { lang } = useLang();
    const [posts, setPosts] = useState([]);
    const [category, setCategory] = useState("all");
    const cats = CATEGORIES[lang] || CATEGORIES.en;

    useEffect(() => {
        const url = category === "all" ? "/blog" : `/blog?category=${category}`;
        api.get(url).then((r) => setPosts(r.data)).catch(() => setPosts([]));
    }, [category]);

    const title = lang === "al" ? "Blog & Udhëzues" : lang === "it" ? "Blog & Guide" : lang === "de" ? "Blog & Reiseführer" : "Blog & Guides";
    const subtitle = lang === "al"
        ? "Histori, këshilla dhe udhëzues nga Rivierës Shqiptare"
        : lang === "it"
          ? "Storie, consigli e guide dalla Riviera Albanese"
          : lang === "de"
            ? "Geschichten, Tipps und Reiseführer von der Albanischen Riviera"
            : "Stories, tips and guides from the Albanian Riviera";

    return (
        <div data-testid="blog-page" className="bg-[#FDFBF7]">
            <SEO
                title={title}
                description={subtitle}
                path="/blog"
            />

            {/* Hero */}
            <section className="relative h-[40vh] min-h-[300px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${imageUrl("seed:gallery_exterior")})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 pt-32 w-full">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">Blog</span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {title}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {subtitle}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Filters */}
            <section className="py-10 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-testid="blog-filters">
                    {Object.entries(cats).map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setCategory(key)}
                            data-testid={`blog-cat-${key}`}
                            className={`px-5 py-2 rounded-full text-sm tracking-wide transition-all ${
                                category === key
                                    ? "bg-[#0F3A59] text-white"
                                    : "bg-white border border-[#E5E0D8] text-[#0F3A59] hover:border-[#0F3A59]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Posts grid */}
            <section className="pb-24 px-6 md:px-12 max-w-7xl mx-auto">
                {posts.length === 0 ? (
                    <div className="text-center py-20 text-[#1C1C1C]/60 font-light">
                        {lang === "al"
                            ? "Nuk ka ende postime në këtë kategori."
                            : lang === "it"
                              ? "Nessun articolo in questa categoria."
                              : lang === "de"
                                ? "Noch keine Beiträge in dieser Kategorie."
                                : "No posts in this category yet."}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {posts.map((p, i) => (
                            <motion.article
                                key={p.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08, duration: 0.5 }}
                                data-testid={`blog-card-${p.slug}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500"
                            >
                                <Link to={`/blog/${p.slug}`} className="block">
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            decoding="async"
                                            loading="lazy"
                                            src={imageUrl(p.cover_image, imageUrl("seed:gallery_exterior"))}
                                            alt={tField(p, "title", lang)}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-7">
                                        <div className="flex items-center gap-3 text-xs text-[#1C1C1C]/55 mb-3">
                                            <span className="uppercase tracking-wider text-[#CBA871]">
                                                {cats[p.category] || cats.general}
                                            </span>
                                            {p.published_at && (
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={11} />
                                                    {formatDate(p.published_at, lang)}
                                                </span>
                                            )}
                                        </div>
                                        <h2 className="font-serif text-2xl text-[#0F3A59] leading-tight">
                                            {tField(p, "title", lang)}
                                        </h2>
                                        <p className="text-sm text-[#1C1C1C]/65 font-light mt-3 line-clamp-3">
                                            {tField(p, "excerpt", lang)}
                                        </p>
                                        <span className="inline-flex items-center gap-1.5 text-sm text-[#0F3A59] font-medium mt-5 group-hover:gap-2.5 transition-all">
                                            {lang === "al" ? "Lexo më shumë" : lang === "it" ? "Continua" : lang === "de" ? "Weiterlesen" : "Read more"}
                                            <ArrowRight size={14} />
                                        </span>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
